import { motion } from "framer-motion";
import { Clapperboard, Youtube, ExternalLink, Film, Eye, Sparkles, Play } from "lucide-react";
import { useYouTube } from "../../hooks/useYouTube";

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "";

const cleanTitle = (t) => t.replace(/une journée avec un dj\s*:?\s*[➡️→-]*\s*/i, "").replace(/[✅➡️]/g, "").trim();

const CHANNEL_URL = "https://www.youtube.com/channel/UCQ8QnDAse7pVx0htDp6vIAg";
const SERIES_PAGE_URL = "https://www.asanimation61.com/blank";

const EPISODES = [
  {
    id: "7XtZ_w5C630",
    label: "Épisode 1",
    title: "Là où tout a commencé",
  },
  {
    id: "mpRDr-VE2FI",
    label: "Épisode 99",
    title: "Mariage de rêve… tout bascule en quelques secondes",
  },
];

const FACTS = [
  { icon: <Film size={16} />, value: "100+", label: "épisodes" },
  { icon: <Eye size={16} />, value: "Sans filtre", label: "les vraies coulisses" },
  { icon: <Sparkles size={16} />, value: "Humour", label: "pédagogie & passion" },
];

export const SeriesSection = () => {
  const { episodes } = useYouTube();
  return (
    <section
      id="serie"
      data-testid="series-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-black border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_20%,rgba(255,0,0,0.10),transparent_55%),radial-gradient(ellipse_at_80%_80%,rgba(212,175,55,0.12),transparent_55%)]" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <span className="eyebrow inline-flex items-center gap-2">
            <Clapperboard size={13} /> Série originale · YouTube
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Créateur de la série{" "}
            <span className="gold-text-gradient">« Une journée avec un DJ »</span>
          </h2>
          <p className="mt-6 text-zinc-300 leading-relaxed text-base md:text-lg">
            Adrien Sebert est le créateur de « Une journée avec un DJ », une série
            vidéo qui vous plonge dans les vraies coulisses du métier de DJ
            événementiel : installation, préparation, imprévus, ambiance, conseils
            et moments forts, avant, pendant et après chaque prestation.
          </p>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Avec plus de 100 épisodes, la série est devenue un rendez-vous
            incontournable pour découvrir le métier autrement — sans filtre, avec
            humour, pédagogie et passion.
          </p>
        </motion.div>

        <div className="mt-10 flex flex-wrap gap-3">
          {FACTS.map((f, i) => (
            <motion.div
              key={f.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`series-fact-${i}`}
              className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.02]"
            >
              <span className="text-[#D4AF37]">{f.icon}</span>
              <span className="text-white font-semibold text-sm">{f.value}</span>
              <span className="text-zinc-400 text-sm">{f.label}</span>
            </motion.div>
          ))}
        </div>

        {episodes[0] && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
            data-testid="series-featured"
            className="mt-12 max-w-md"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FF0000]/15 text-[#FF4D4D] text-[10px] tracking-[0.28em] uppercase font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF0000] animate-pulse" /> Dernier épisode
              </span>
              <span className="text-xs text-zinc-500">{formatDate(episodes[0].published)}</span>
            </div>
            <div className="relative rounded-3xl overflow-hidden border border-[#D4AF37]/30 gold-glow bg-black">
              <div className="aspect-video w-full relative">
                <iframe
                  src={`https://www.youtube.com/embed/${episodes[0].id}?rel=0&modestbranding=1`}
                  title={episodes[0].title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                  loading="lazy"
                  data-testid="series-featured-iframe"
                />
              </div>
            </div>
            <div className="mt-3 px-1 text-white font-semibold text-base" data-testid="series-featured-title">
              {cleanTitle(episodes[0].title)}
            </div>
          </motion.div>
        )}

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {EPISODES.map((ep, i) => (
            <motion.div
              key={ep.id}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.2, 0.8, 0.2, 1] }}
              data-testid={`series-episode-${i}`}
              className="group"
            >
              <div className="relative rounded-3xl overflow-hidden border border-[#D4AF37]/25 gold-glow bg-black">
                <div className="aspect-video w-full relative">
                  <iframe
                    src={`https://www.youtube.com/embed/${ep.id}?rel=0&modestbranding=1`}
                    title={`Une journée avec un DJ – ${ep.label}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                    loading="lazy"
                    data-testid={`series-episode-iframe-${i}`}
                  />
                </div>
              </div>
              <div className="mt-4 px-1">
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]">
                  {ep.label}
                </div>
                <div className="mt-1 text-white font-semibold">{ep.title}</div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-14">
          <div className="flex items-center justify-between gap-4 mb-5">
            <div>
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]">Derniers épisodes</div>
              <div className="mt-1 text-white font-semibold">La série continue chaque mois</div>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-zinc-400">
              <span className="w-2 h-2 rounded-full bg-[#FF0000] animate-pulse" /> Mis à jour automatiquement
            </span>
          </div>
          <div
            data-testid="series-latest-grid"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4"
          >
            {episodes.slice(1).map((ep, i) => (
              <motion.a
                key={ep.id}
                href={`https://www.youtube.com/watch?v=${ep.id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`series-latest-${i}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group block rounded-xl overflow-hidden border border-white/10 hover:border-[#FF4D4D]/50 bg-[#0A0A0A] transition-all hover:-translate-y-1"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={`https://i.ytimg.com/vi/${ep.id}/mqdefault.jpg`}
                    alt={ep.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="w-10 h-10 rounded-full bg-[#FF0000] text-white flex items-center justify-center shadow-lg opacity-90 group-hover:scale-110 transition-transform">
                      <Play size={16} className="translate-x-0.5 fill-current" />
                    </span>
                  </div>
                </div>
                <div className="p-3">
                  <div className="text-xs text-white font-medium leading-snug line-clamp-2">{cleanTitle(ep.title)}</div>
                  <div className="mt-1.5 text-[10px] text-zinc-500 uppercase tracking-wider">{formatDate(ep.published)}</div>
                </div>
              </motion.a>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 flex flex-col sm:flex-row gap-4"
        >
          <a
            href={CHANNEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="series-youtube-channel-btn"
            className="btn-gold rounded-full px-7 py-3.5 text-sm inline-flex items-center justify-center gap-2"
          >
            <Youtube size={18} /> Voir tous les épisodes sur YouTube
          </a>
          <a
            href={SERIES_PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="series-page-btn"
            className="btn-outline-gold rounded-full px-7 py-3.5 text-sm inline-flex items-center justify-center gap-2"
          >
            <ExternalLink size={16} /> Page officielle de la série
          </a>
        </motion.div>
      </div>
    </section>
  );
};
